import { FC, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MenuProps } from "@dirootie-web/elements-types";
import { createMenuItems } from "./helper";
import { IconRT } from "../../../components-common/icons/Icon";
import { RootieComponent } from "../pages";
import { useUserContext } from "../../../_runtime/iam/session.context";
import { OverlayTooltip } from "../../../components-domain/generation-domain/specifications/OverlayTooltip";
import Tooltip from "react-bootstrap/Tooltip";
import { useMsal } from "@azure/msal-react";

/**
 * Retrieves menu icons based on the current URL parameters, pathname, and user role.
 *
 * @param {Object} urlParams - The URL parameters.
 * @param {string} pathname - The current pathname.
 * @param {string | undefined} role - The user role.
 * @returns {Array} - The array of menu items.
 */
const getIcons = (
  urlParams: any,
  pathname: string,
  role: string | undefined
) => {
  if (!role) return [];

  if (pathname.includes(`courses/${urlParams["id"]}`)) {
    const defaultItems = [
      {
        id: "menu.course.resources",
        to: `/courses/${urlParams["id"]}/resources`,
        label: "Resources",
        icon: "resources",
      },
      {
        id: "menu.course.assignments",
        to: `/courses/${urlParams["id"]}/assignments`,
        label: "Assignments",
        icon: "assignments",
      },
    ];

    if (role === "STUDENT") {
      return defaultItems;
    }

    return [
      {
        id: "menu.course.dashboard",
        to: `/courses/${urlParams["id"]}`,
        label: "Dashboard",
        icon: "dashboard",
      },
      {
        id: "menu.course.students",
        to: `/courses/${urlParams["id"]}/people`,
        label: "People",
        icon: "student",
      },
      {
        id: "menu.course.groups",
        to: `/courses/${urlParams["id"]}/groups`,
        label: "Groups",
        icon: "students",
      },
      ...defaultItems,
    ];
  }
};

/**
 * NavBarLeft Component
 *
 * Renders the left navigation bar with menu items based on the user's role and current location.
 *
 * @param {MenuProps & { children?: any }} props - The props for the component.
 * @returns {JSX.Element} - The rendered navigation bar.
 */
export const NavBarLeft: FC<MenuProps & { children?: any }> = (props) => {
  const nav = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { role } = useUserContext();
  const { instance } = useMsal();
  const items: any = getIcons(params, location.pathname, role);
  const body = items?.length > 0 ? createMenuItems(items) : <></>;
  const account = instance.getActiveAccount();

  const handleLogout = () => {
    instance.logoutRedirect({
      account: account,
      logoutHint: account?.idTokenClaims!["login_hint"],
      postLogoutRedirectUri: "/"
    });
  };

  return (
    <div className="d-flex flex-column align-items-center justify-content-between h-100">
      <div className="d-flex flex-column gap-2">{body}</div>
      <div></div>

      <div className="d-flex flex-column align-items-center gap-2">
        <>
          {props.children.map((x: any, index: number) => (
            <RootieComponent key={index} component={x} />
          ))}
        </>
        <OverlayTooltip
          component={() => (
            <Tooltip className="tooltip-button" style={{ position: "fixed" }}>
              Help
            </Tooltip>
          )}
        >
          <button
            className={`edb-icon-sidebar ${
              location.pathname.includes("support") ? "active" : ""
            }`}
          >
            <IconRT
              kind="support"
              color="black"
              onClick={() => nav("/support")}
            ></IconRT>
          </button>
        </OverlayTooltip>
        <OverlayTooltip
          component={() => (
            <Tooltip className="tooltip-button" style={{ position: "fixed" }}>
              Logout
            </Tooltip>
          )}
        >
          <button className="edb-icon-sidebar">
            <IconRT
              kind="logout"
              color="black"
              onClick={() => handleLogout()}
            ></IconRT>
          </button>
        </OverlayTooltip>
      </div>
    </div>
  );
};
